import React, { useEffect, useMemo, useState } from "react";
import StakingService from "../../../api/services/StakingService";
import {
  formatStakeBalance,
  getPackageIcon,
  getTotalInvestedAmount,
  parseDec,
} from "./stakingStakeHelpers";
import "./staking.css";

const OverviewRow = ({ label, value, highlight, danger }) => (
  <div className="stake-overview-row">
    <span className="stake-overview-row__label">{label}</span>
    <span
      className={`stake-overview-row__value${
        highlight ? " stake-overview-row__value--green" : danger ? " stake-overview-row__value--danger" : ""
      }`}
    >
      {value}
    </span>
  </div>
);

const formatCancelDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCancelEstimates = (position) => {
  const pkg = position?.packageId;
  const pkgObj = pkg && typeof pkg === "object" ? pkg : {};
  const currency = position?.currency || pkgObj.currency || "";
  const invested = parseFloat(getTotalInvestedAmount(position)) || 0;
  const penaltyPercent = parseFloat(parseDec(pkgObj.earlyWithdrawalPenalty)) || 0;
  const penaltyAmount = (invested * penaltyPercent) / 100;
  const refundAmount = Math.max(invested - penaltyAmount, 0);
  const rewardEarned = parseFloat(parseDec(position?.totalRewardEarned)) || 0;

  return {
    currency,
    invested,
    penaltyPercent,
    penaltyAmount,
    refundAmount,
    rewardEarned,
  };
};

const getSuccessCancelSummary = (position, successData = {}) => {
  const pkg = position?.packageId;
  const pkgObj = pkg && typeof pkg === "object" ? pkg : {};
  const currency = successData.currency || position?.currency || pkgObj.currency || "";
  const invested =
    parseFloat(parseDec(successData.totalInvestedAmount)) ||
    parseFloat(getTotalInvestedAmount(position)) ||
    0;
  const penaltyPercent = parseFloat(parseDec(pkgObj.earlyWithdrawalPenalty)) || 0;
  const penaltyAmount = (invested * penaltyPercent) / 100;
  const refundAmount = Math.max(invested - penaltyAmount, 0);
  const rewardEarned =
    parseFloat(parseDec(successData.totalRewardEarned)) ||
    parseFloat(parseDec(position?.totalRewardEarned)) ||
    0;

  return {
    currency,
    invested,
    penaltyPercent,
    penaltyAmount,
    refundAmount,
    rewardEarned,
  };
};

const PenaltySummaryRows = ({ estimates, currency, showReward = false, cancelReason = "" }) => (
  <>
    <OverviewRow
      label="Total Invested"
      value={`${formatStakeBalance(estimates.invested)} ${currency}`}
    />
    <OverviewRow
      label="Penalty"
      value={`${estimates.penaltyPercent}%`}
      danger
    />
    <OverviewRow
      label="Penalty Amount"
      value={`${formatStakeBalance(estimates.penaltyAmount)} ${currency}`}
      danger
    />
    <OverviewRow
      label="Refund Amount"
      value={`${formatStakeBalance(estimates.refundAmount)} ${currency}`}
      highlight
    />
    {showReward ? (
      <OverviewRow
        label="Reward Earned"
        value={`${formatStakeBalance(estimates.rewardEarned)} ${currency}`}
      />
    ) : null}
    {cancelReason.trim() ? (
      <OverviewRow label="Cancel Reason" value={cancelReason.trim()} />
    ) : null}
  </>
);

const CancelStakingModal = ({ position, onClose, onSuccess }) => {
  const [step, setStep] = useState("reason");
  const [cancelReason, setCancelReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);

  const pkg = position?.packageId || {};
  const estimates = useMemo(() => getCancelEstimates(position), [position]);

  const handleSuccessDone = () => {
    onSuccess?.();
    onClose?.();
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || loading) return;
      if (step === "success") {
        handleSuccessDone();
        return;
      }
      if (step === "confirm") {
        setStep("reason");
        setError("");
        return;
      }
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, loading, onClose, handleSuccessDone]);

  const handleReasonContinue = () => {
    setError("");
    setStep("confirm");
  };

  const submitCancel = async () => {
    if (!position?._id) return;
    setLoading(true);
    setError("");
    try {
      const res = await StakingService.cancel(String(position._id), cancelReason);
      if (res?.success) {
        setSuccessData(res.data || {});
        setStep("success");
      } else {
        setError(res?.message || "Cancel failed. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!position) return null;

  const { currency } = estimates;

  if (step === "success") {
    const data = successData || {};
    const successSummary = getSuccessCancelSummary(position, data);
    const successCurrency = successSummary.currency;

    return (
      <div className="stake-success-overlay" onClick={handleSuccessDone}>
        <div className="stake-success-content" onClick={(e) => e.stopPropagation()}>
          <div className="stake-success-icon stake-success-icon--cancel" aria-hidden="true">
            <i className="ri-checkbox-circle-fill" />
          </div>
          <h3 className="stake-success-title">Staking Cancelled!</h3>
          <p className="stake-success-message">
            Your staking position has been cancelled successfully.
          </p>

          <div className="stake-overview-body stake-overview-body--inline">
            <PenaltySummaryRows estimates={successSummary} currency={successCurrency} showReward />
            <OverviewRow label="Cancelled At" value={formatCancelDate(data.cancelledAt)} />
          </div>

          <button type="button" className="mint-btn-confirm stake-success-btn" onClick={handleSuccessDone}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (step === "confirm") {
    return (
      <div className="stake-overview-overlay" onClick={loading ? undefined : () => { setStep("reason"); setError(""); }}>
        <div className="stake-overview-content" onClick={(e) => e.stopPropagation()}>
          <div className="stake-overview-header">
            <div className="stake-overview-header__left">
              <div className="stake-overview-title">
                <img
                  src={getPackageIcon({ ...pkg, currency })}
                  alt={currency}
                  className="mint-title-icon"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
                  }}
                />
                <span>{currency} Cancel Overview</span>
              </div>
            </div>
            <button
              type="button"
              className="mint-modal-close"
              disabled={loading}
              onClick={() => { setStep("reason"); setError(""); }}
            >
              <i className="ri-close-line" />
            </button>
          </div>

          <p className="stake-overview-subtitle">
            Please review the cancellation details before confirming. This action cannot be undone.
          </p>

          <div className="stake-overview-body">
            <OverviewRow label="Currency" value={currency || "—"} />
            <PenaltySummaryRows
              estimates={estimates}
              currency={currency}
              showReward
              cancelReason={cancelReason}
            />
          </div>

          {error ? <div className="stake-overview-error">{error}</div> : null}

          <div className="stake-overview-actions">
            <button
              type="button"
              className="mint-btn-cancel"
              disabled={loading}
              onClick={() => { setStep("reason"); setError(""); }}
            >
              Back
            </button>
            <button
              type="button"
              className="mint-btn-confirm mint-btn-confirm--danger"
              disabled={loading}
              onClick={submitCancel}
            >
              {loading ? "Processing..." : "Confirm Cancel"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mint-modal-overlay" onClick={onClose}>
      <div className="mint-modal-content cancel-staking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mint-modal-header">
          <div className="mint-modal-title">
            <img
              src={getPackageIcon({ ...pkg, currency })}
              alt={currency}
              className="mint-title-icon"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
              }}
            />
            <span>Cancel {currency} Staking</span>
          </div>
          <button type="button" className="mint-modal-close" onClick={onClose}>
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="cancel-staking-body">
          <p className="cancel-staking-warning">
            <i className="ri-error-warning-line" aria-hidden="true" />
            <span>Cancelling will stop earning rewards and may apply an early penalty.</span>
          </p>

          <div className="mint-amount-section">
            <label className="mint-section-label" htmlFor="cancel-reason">
              Cancel Reason <span className="cancel-staking-optional">(Optional)</span>
            </label>
            <textarea
              id="cancel-reason"
              className="cancel-staking-reason"
              placeholder="e.g. Need funds urgently"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="stake-overview-body stake-overview-body--compact">
            <PenaltySummaryRows estimates={estimates} currency={currency} />
          </div>
        </div>

        <div className="mint-modal-footer">
          <div className="mint-actions">
            <button type="button" className="mint-btn-cancel" onClick={onClose}>
              Close
            </button>
            <button type="button" className="mint-btn-confirm mint-btn-confirm--danger" onClick={handleReasonContinue}>
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelStakingModal;
